import { Checkbox } from '@carbon/react';
import { format0 } from 'utils/number';
import type { SpatialLayerOption } from './types';
import styles from './spatial-layer-picker.module.scss';

/** The picker's controlled selection — selected layer ids + the chosen attribute ids per layer. */
export interface SpatialLayerSelectionState {
  layerIds: string[];
  attributesByLayer: Record<string, string[]>;
}

export interface SpatialLayerPickerProps {
  /** The exportable layers (roster) to render. */
  layers: SpatialLayerOption[];
  /** The currently-selected layer ids. */
  layerIds: string[];
  /**
   * The chosen attribute ids per layer. A layer absent from the map (or mapped to `[]`) is
   * geometry-only; this map is the single source of truth for what is checked.
   */
  attributesByLayer: Record<string, string[]>;
  /** Emits the next `{ layerIds, attributesByLayer }` on every header / attribute toggle. */
  onChange: (next: SpatialLayerSelectionState) => void;
}

/**
 * The shared per-layer spatial attribute picker (INS-13268). A fully-controlled, presentational grid:
 * one fieldset per layer with a header checkbox (name + record count + attribute count) above an
 * inline grid of attribute checkboxes. One copy, rendered by both the shared ExportSpatialData dialog
 * (Map Query / Areas / Type Curves) and the Eval 3 content-export modal, so a fix to the selection
 * rules, the a11y, or the markup lands once.
 *
 * Behaviour:
 *  - the header is select-all / clear-all for its attributes: turning it on selects every attribute,
 *    turning it off clears them and drops the layer from the export;
 *  - ticking any attribute re-includes a layer whose header is off (with just that attribute);
 *  - a checked layer with every attribute cleared is a valid geometry-only export (geometry is always
 *    included).
 * The record-count prefix shows only for a layer that carries a `count`.
 *
 * @param {SpatialLayerPickerProps} props - The roster, the controlled selection, and the change handler.
 * @return {JSX.Element} The layer/attribute checkbox grid.
 */
export function SpatialLayerPicker({
  layers,
  layerIds,
  attributesByLayer,
  onChange,
}: SpatialLayerPickerProps) {
  return (
    <>
      {layers.map((layer) => {
        const checked = layerIds.includes(layer.id);
        const selected = attributesByLayer[layer.id] ?? [];
        const count = layer.attributes.length;
        const countPrefix =
          layer.count === undefined ? '' : `${format0(layer.count)} `;
        return (
          <fieldset
            key={layer.id}
            className={styles.layerGroup}
            aria-label={`${layer.label} attributes`}
          >
            <Checkbox
              id={`spatial-layer-${layer.id}`}
              className={styles.layerCheckbox}
              labelText={`${countPrefix}${layer.label} (${count} Attribute${count === 1 ? '' : 's'})`}
              checked={checked}
              // Partial selection reads as indeterminate: the layer is on but only some of its
              // attributes are chosen. A full selection or a geometry-only layer shows a plain check.
              indeterminate={
                checked && selected.length > 0 && selected.length < count
              }
              onChange={(_event, { checked: next }) => {
                onChange({
                  layerIds: next
                    ? [...new Set([...layerIds, layer.id])]
                    : layerIds.filter((id) => id !== layer.id),
                  attributesByLayer: {
                    ...attributesByLayer,
                    // Select-all on check, clear-all on uncheck.
                    [layer.id]: next
                      ? layer.attributes.map((attribute) => attribute.id)
                      : [],
                  },
                });
              }}
            />
            <div className={styles.attributeGrid}>
              {layer.attributes.map((attribute) => (
                <Checkbox
                  key={attribute.id}
                  id={`spatial-attr-${layer.id}-${attribute.id}`}
                  labelText={attribute.label}
                  checked={selected.includes(attribute.id)}
                  onChange={(_event, { checked: on }) => {
                    const nextForLayer = on
                      ? [...new Set([...selected, attribute.id])]
                      : selected.filter((id) => id !== attribute.id);
                    onChange({
                      // Ticking an attribute re-includes a layer whose header is off.
                      layerIds:
                        on && !layerIds.includes(layer.id)
                          ? [...layerIds, layer.id]
                          : layerIds,
                      attributesByLayer: {
                        ...attributesByLayer,
                        [layer.id]: nextForLayer,
                      },
                    });
                  }}
                />
              ))}
            </div>
          </fieldset>
        );
      })}
    </>
  );
}
